// src/services/tripPhotoActions.ts
import { generateClient } from 'aws-amplify/api';
import { deletePhotoWithAuth } from '../graphql/mutations';

const client = generateClient();

/**
 * Delete a single photo
 */
export const deletePhoto = async (photoId: string): Promise<any> => {
  try {
    const response = await client.graphql({
      query: deletePhotoWithAuth,
      variables: { photoId },
      authMode: "userPool"
    });
    
    return response.data.deletePhotoWithAuth;
  } catch (error) {
    console.error('Error deleting photo:', error);
    throw error;
  }
};

/**
 * Delete multiple photos
 * Returns an array of successful deletions and errors
 */
export const deletePhotos = async (photoIds: string[]): Promise<{
  successful: string[];
  failed: { id: string; error: any }[];
}> => {
  const successful: string[] = [];
  const failed: { id: string; error: any }[] = [];
  
  // Process each photo deletion sequentially
  for (const photoId of photoIds) {
    try {
      await deletePhoto(photoId);
      successful.push(photoId);
    } catch (error) {
      failed.push({ id: photoId, error });
    }
  }
  
  return { successful, failed };
};

/**
 * Download a single photo
 */
export const downloadPhoto = async (photoId: string, photoUrl: string, fileName?: string): Promise<void> => {
    try {
      // Detect if running on iOS Safari or other mobile browser with download limitations
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isIOS || isMobile) {
        // For mobile devices, especially iOS, open the image in a new tab
        // This gives the user the option to save it using the browser's native controls
        const newTab = window.open(photoUrl, '_blank');
        if (!newTab) {
          // If popup is blocked, try to open in same window or provide instructions
          alert('Please long-press the photo and select "Save Image" from the menu to download.');
          window.location.href = photoUrl;
        }
      } else {
        // Desktop approach with a temporary anchor element
        const link = document.createElement('a');
        link.href = photoUrl;
        link.download = fileName || `photo-${photoId}`;  // Make sure there's always a filename
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading photo:', error);
      // Fallback for any cases where the above methods fail
      window.open(photoUrl, '_blank');
    }
  };

/**
 * Download multiple photos as a zip file (using backend-generated URL)
 */
export const downloadPhotosAsZip = async (
  tripId: string,
  photoIds: string[],
  tripName?: string
): Promise<void> => {
  try {
    // This is a placeholder for the actual implementation that would call the
    // backend API to generate a presigned URL for a ZIP file
    // You would need to implement the actual GraphQL query here
    
    // Example query that could be implemented:
    /*
    const response: any = await client.graphql({
      query: generatePhotoDownload,
      variables: {
        photoIds,
        tripId,
        zipDownload: true
      },
      authMode: "userPool"
    });
    
    const { zipUrl } = response.data.generatePhotoDownload;
    
    if (zipUrl) {
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = zipUrl;
      link.setAttribute('download', `${(tripName || 'photos').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${photoIds.length > 1 ? 'photos' : 'photo'}.zip`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    */
    
    // For now, throw an unimplemented error
    throw new Error('Download multiple photos functionality is not yet implemented');
  } catch (error) {
    console.error('Error downloading photos as zip:', error);
    throw error;
  }
};